const SELECTOR = {
    gallery: '.CE_GALLERY .CE_GALLERY_ITEMS',
    slider: '.post-slider .post-slider-items',
};

function getCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    return document.cookie
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.startsWith(prefix))
        ?.slice(prefix.length);
}

function setCookie(name, value, days = 365) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

class OeiiCarousel {
    constructor(element, options) {
        this.element = element;
        this.options = {
            autoplay: false,
            autoplayTimeout: 15000,
            dots: false,
            margin: 0,
            responsiveItems: () => 1,
            ...options,
        };
        this.originals = Array.from(element.children);
        this.cloneCount = Math.min(this.options.cloneCount ?? 4, this.originals.length);
        this.current = 0;
        this.timer = null;

        if (!this.originals.length) {
            return;
        }

        this.render();
        this.resizeObserver = new ResizeObserver(() => this.layout(false));
        this.resizeObserver.observe(this.element);
        this.layout(false);
        this.startAutoplay();
    }

    createItem(content, cloned = false) {
        const item = document.createElement('div');
        item.className = `owl-item${cloned ? ' cloned' : ''}`;
        item.append(content.cloneNode(true));
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
        return item;
    }

    render() {
        this.element.replaceChildren();
        this.element.classList.add('owl-loaded', 'owl-drag');
        this.element.setAttribute('role', 'region');
        this.element.setAttribute('aria-roledescription', 'Karussell');
        this.element.setAttribute('aria-label', this.options.label || 'Bildergalerie');

        this.stage = document.createElement('div');
        this.stage.className = 'owl-stage';
        this.stage.style.transition = 'all 0s ease 0s';

        const leading = this.originals.slice(-this.cloneCount);
        const trailing = this.originals.slice(0, this.cloneCount);
        leading.forEach((node) => this.stage.append(this.createItem(node, true)));
        this.originals.forEach((node) => this.stage.append(this.createItem(node)));
        trailing.forEach((node) => this.stage.append(this.createItem(node, true)));

        this.outer = document.createElement('div');
        this.outer.className = 'owl-stage-outer';
        this.outer.append(this.stage);
        this.element.append(this.outer);

        this.nav = document.createElement('div');
        this.nav.className = 'owl-nav';
        this.previousButton = this.createNavButton('owl-prev', 'fa-angle-left', 'vorherige');
        this.nextButton = this.createNavButton('owl-next', 'fa-angle-right', 'nächste');
        this.nav.append(this.previousButton, this.nextButton);
        this.element.append(this.nav);

        this.dots = document.createElement('div');
        this.dots.className = `owl-dots${this.options.dots ? '' : ' disabled'}`;
        if (this.options.dots) {
            this.originals.forEach((_node, index) => {
                const dot = document.createElement('button');
                dot.type = 'button';
                dot.className = `owl-dot${index === 0 ? ' active' : ''}`;
                dot.setAttribute('aria-label', `Folie ${index + 1} anzeigen`);
                dot.append(document.createElement('span'));
                dot.addEventListener('click', () => this.goTo(index));
                this.dots.append(dot);
            });
        }
        this.element.append(this.dots);

        this.previousButton.addEventListener('click', () => this.previous());
        this.nextButton.addEventListener('click', () => this.next());
        this.element.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                this.previous();
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                this.next();
            }
        });
        this.element.addEventListener('focusin', () => this.stopAutoplay());
        this.element.addEventListener('focusout', () => this.startAutoplay());
    }

    createNavButton(className, iconName, label) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        button.innerHTML = `<i class="fa ${iconName}" aria-hidden="true"></i><span class="sr-only">${label}</span>`;
        return button;
    }

    layout(animate = true) {
        if (!this.stage) {
            return;
        }

        const viewportWidth = this.element.clientWidth;
        const visibleItems = this.options.responsiveItems(window.innerWidth);
        const itemWidth = (viewportWidth + this.options.margin) / visibleItems - this.options.margin;
        const step = itemWidth + this.options.margin;
        const items = Array.from(this.stage.children);

        items.forEach((item) => {
            item.style.width = `${itemWidth}px`;
            item.style.marginRight = `${this.options.margin}px`;
        });
        this.stage.style.width = `${items.length * step}px`;
        this.stage.style.transition = animate ? 'all 0.25s ease 0s' : 'all 0s ease 0s';
        const centerOffset = (viewportWidth - itemWidth) / 2;
        const x = Math.ceil(-((this.cloneCount + this.current) * step) + centerOffset);
        this.stage.style.transform = `translate3d(${x}px, 0px, 0px)`;

        items.forEach((item) => item.classList.remove('active', 'center'));
        const start = this.cloneCount + this.current;
        for (let index = 0; index < visibleItems; index += 1) {
            items[start + index]?.classList.add('active');
        }
        items[start]?.classList.add('center');
        // The carousel only needs the current view and its neighbours. Native
        // lazy loading alone still fetches far-off slides on wide stages.
        items.slice(Math.max(0, start - 1), start + visibleItems + 1).forEach((item) => {
            item.querySelectorAll('img[data-carousel-src]').forEach((image) => {
                image.loading = 'eager';
                if (image.dataset.carouselSrcset) image.srcset = image.dataset.carouselSrcset;
                image.src = image.dataset.carouselSrc;
                delete image.dataset.carouselSrc;
                delete image.dataset.carouselSrcset;
            });
        });
        items.forEach((item) => {
            const active = item.classList.contains('active');
            item.inert = !active;
            item.setAttribute('aria-hidden', active ? 'false' : 'true');
            item.setAttribute('tabindex', active ? '0' : '-1');
        });

        Array.from(this.dots?.children || []).forEach((dot, index) => {
            dot.classList.toggle('active', index === this.current);
            dot.setAttribute('aria-current', index === this.current ? 'true' : 'false');
        });

        requestAnimationFrame(() => this.positionGalleryNavigation());
    }

    positionGalleryNavigation() {
        const gallery = this.element.closest('.CE_GALLERY');
        const activeItem = this.stage?.querySelector('.owl-item.active');
        const image = this.stage?.querySelector('.owl-item.active img');
        if (!gallery || !activeItem || !image || !this.nav || !this.outer) {
            return;
        }
        // Owl's autoHeight initializes before an off-screen lazy image paints.
        // Its established height is therefore the item's non-image remainder;
        // retain that exact crop after the vanilla migration.
        const itemHeight = activeItem.getBoundingClientRect().height;
        const imageHeight = image.getBoundingClientRect().height;
        this.outer.classList.add('owl-height');
        this.outer.style.height = `${Math.max(0, itemHeight - imageHeight)}px`;
        this.nav.style.marginTop = `${-this.nav.getBoundingClientRect().height / 2}px`;
    }

    goTo(index) {
        const count = this.originals.length;
        this.current = (index + count) % count;
        this.layout(true);
        this.restartAutoplay();
    }

    next() {
        this.goTo(this.current + 1);
    }

    previous() {
        this.goTo(this.current - 1);
    }

    startAutoplay() {
        if (!this.options.autoplay || this.timer || this.originals.length < 2 || this.element.matches(':focus-within')) {
            return;
        }
        this.timer = window.setInterval(() => this.next(), this.options.autoplayTimeout);
    }

    stopAutoplay() {
        window.clearInterval(this.timer);
        this.timer = null;
    }

    restartAutoplay() {
        this.stopAutoplay();
        this.startAutoplay();
    }
}

function initCarousels() {
    document.querySelectorAll(SELECTOR.slider).forEach((element) => {
        new OeiiCarousel(element, {
            label: 'Aktuelle Beiträge',
            autoplay: true,
            autoplayTimeout: 15000,
            dots: false,
            margin: 5,
            responsiveItems: (width) => {
                if (width >= 1270) return 2;
                if (width >= 1080) return 3;
                if (width >= 900) return 2;
                return 1;
            },
        });
    });

    document.querySelectorAll(SELECTOR.gallery).forEach((element) => {
        new OeiiCarousel(element, {dots: true, cloneCount: 3});
    });
}

function initHeaderSearch() {
    document.querySelectorAll('a.l-header-search__loupe').forEach((loupe) => {
        loupe.addEventListener('click', (event) => {
            event.preventDefault();
            const wrapper = loupe.closest('.l-header-search');
            const input = wrapper?.querySelector('.l-header-search__input');
            const resultForm = wrapper?.querySelector('.tx-solr-search-form-pi-results');
            const submit = wrapper?.querySelector('.l-header-search__submit, .tx-solr-submit');
            if (!input) return;

            if (input.classList.contains('show') && input.value !== '') {
                submit?.click();
                return;
            }

            resultForm?.classList.toggle('d-none');
            input.classList.toggle('show');
            input.value = '';
            input.focus();
        });
    });

    document.querySelectorAll('.tx-solr-submit').forEach((submit) => {
        submit.addEventListener('focusout', (event) => {
            if (event.shiftKey) return;
            const wrapper = submit.closest('.l-header-search');
            wrapper?.querySelector('.l-header-search__input')?.classList.remove('show');
            wrapper?.querySelector('.tx-solr-search-form-pi-results')?.classList.add('d-none');
        });
    });
}

function replaceWithHtml(element, html) {
    const template = document.createElement('template');
    template.innerHTML = html || '';
    element.after(template.content);
}

function loadInfographic(trigger) {
    const overlay = trigger.closest('[data-infografik]') || trigger.parentElement?.parentElement;
    if (!overlay) return;
    const image = overlay.previousElementSibling;
    replaceWithHtml(overlay, overlay.dataset.infografik);
    overlay.remove();
    image?.remove();
}

function createYoutubeFrame(source, start, autoplay, className, width, height) {
    const frame = document.createElement('iframe');
    const separator = source.includes('?') ? '&' : '?';
    frame.src = `${source}${separator}enablejsapi=1&version=3&playerapiid=ytplayer&autoplay=${autoplay ? 1 : 0}&start=${start || 0}`;
    frame.title = 'YouTube-Video';
    frame.width = width;
    frame.height = height;
    frame.setAttribute('frameborder', '0');
    frame.setAttribute('allowfullscreen', 'allowfullscreen');
    frame.className = className;
    return frame;
}

function loadStandaloneVideo(trigger, autoplay) {
    const helper = trigger.closest('[data-youtube]') || trigger.parentElement?.parentElement?.parentElement;
    if (!helper || helper.classList.contains('loaded')) return;
    helper.classList.add('loaded');
    const source = `https://www.youtube-nocookie.com/embed/${helper.dataset.youtube}`;
    helper.prepend(createYoutubeFrame(source, helper.dataset.start, autoplay, 'iframe', 640, 385));
    helper.querySelector('.embed-responsive')?.remove();
    helper.querySelector('.CE_YOUTUBE_GDPR_OVERLAY')?.remove();
}

function loadVideoList(helper, autoplay = false) {
    if (!helper || helper.classList.contains('loaded')) return;
    helper.classList.add('loaded');
    const responsive = helper.querySelector('.embed-responsive');
    if (responsive) {
        responsive.replaceChildren(createYoutubeFrame(helper.dataset.youtube, helper.dataset.start, autoplay, 'embed-responsive-item', 1600, 900));
    }
    helper.querySelector('.CE_YOUTUBE_GDPR_OVERLAY')?.remove();
}

function initExternalMedia() {
    if (getCookie('gdpr-loaded-info')) {
        document.querySelectorAll('.js-loadInfografik').forEach(loadInfographic);
    }
    document.querySelectorAll('.js-loadInfografik').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            loadInfographic(trigger);
            setCookie('gdpr-loaded-info', '1');
        });
    });

    if (getCookie('gdpr-loaded-video')) {
        document.querySelectorAll('.js-loadVideo').forEach((trigger) => loadStandaloneVideo(trigger, false));
    }
    document.querySelectorAll('.js-loadVideo').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            loadStandaloneVideo(trigger, true);
            setCookie('gdpr-loaded-video', '1');
        });
    });
    document.querySelectorAll('.js-loadVideoList').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            document.querySelectorAll('.js-loadVideoList').forEach((item) => loadVideoList(item.closest('.CE_VIDEO_HELPER'), true));
            setCookie('gdpr-loaded-video', '1');
        });
    });
}

function initSkipLinks() {
    document.querySelectorAll('#skipmenu a[href^="#"]').forEach((link) => {
        link.addEventListener('click', (event) => {
            const target = document.getElementById(link.hash.slice(1));
            if (!target) return;
            event.preventDefault();
            const focusTarget = () => {
                const destination = target.id === 'navbarMain' ? target.querySelector('a[href]') || target : target;
                if (!destination.matches('a[href], button, input, select, textarea')) destination.tabIndex = -1;
                destination.focus({preventScroll: true});
                target.scrollIntoView({block: 'start'});
            };
            if (target.id === 'navbarMain' && getComputedStyle(target).display === 'none') {
                target.addEventListener('shown.bs.collapse', focusTarget, {once: true});
                window.bootstrap.Collapse.getOrCreateInstance(target, {toggle: false}).show();
            } else {
                focusTarget();
            }
        });
    });
}

function initToTop() {
    const buttons = document.querySelectorAll('.toTop');
    buttons.forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            window.scrollTo({top: 0, behavior: 'smooth'});
        });
    });
    const update = () => buttons.forEach((button) => {
        button.style.display = window.scrollY > 300 ? 'block' : 'none';
    });
    window.addEventListener('scroll', update, {passive: true});
    update();
}

function initResponsiveTables() {
    document.querySelectorAll('.CE_TEXT .table').forEach((table) => {
        if (table.parentElement?.classList.contains('table-responsive')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'table-responsive';
        table.before(wrapper);
        wrapper.append(table);
    });
}

function initFilters() {
    document.querySelectorAll('.nav.nav-filter button').forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            const section = button.closest('.videolist, .CE_VIDEOLIST') || document;
            const filterGroup = button.closest('.nav-filter');
            filterGroup?.querySelectorAll('button').forEach((filterButton) => {
                const selected = filterButton === button;
                filterButton.classList.toggle('current', selected);
                filterButton.setAttribute('aria-pressed', selected ? 'true' : 'false');
            });
            const filter = button.dataset.filter || '*';
            const filterClass = filter.startsWith('.') ? filter.slice(1) : filter;
            let visibleCount = 0;
            section.querySelectorAll('.video-list-item').forEach((item) => {
                item.hidden = filter !== '*' && !item.classList.contains(filterClass);
                if (!item.hidden) visibleCount += 1;
            });
            const itemLabel = visibleCount === 1
                ? filterGroup?.dataset.filterItemSingular || 'Eintrag'
                : filterGroup?.dataset.filterItemPlural || 'Einträge';
            const category = button.textContent.trim();
            const status = section.querySelector('.filter-status');
            if (status) status.textContent = `${visibleCount} ${itemLabel} für ${category} angezeigt.`;
        });
    });
}

function postToYoutube(frame, command) {
    frame.contentWindow?.postMessage(JSON.stringify({event: 'command', func: command, args: ''}), '*');
}

function initVideoModals() {
    document.querySelectorAll('.modal-video').forEach((modal) => {
        modal.addEventListener('hide.bs.modal', () => {
            modal.querySelectorAll('video').forEach((video) => video.pause());
            modal.querySelectorAll('iframe').forEach((frame) => postToYoutube(frame, 'stopVideo'));
        });
        modal.addEventListener('show.bs.modal', () => {
            if (!modal.querySelector('iframe') && getCookie('gdpr-loaded-video')) {
                loadVideoList(modal.querySelector('.CE_VIDEO_HELPER'));
            }
            modal.querySelectorAll('video').forEach((video) => video.play().catch(() => {}));
            modal.querySelectorAll('iframe').forEach((frame) => postToYoutube(frame, 'playVideo'));
        });
    });
}

function initLazyImages() {
    if ('loading' in HTMLImageElement.prototype) {
        document.querySelectorAll('img[loading="lazy"][data-src]').forEach((image) => {
            image.src = image.dataset.src;
        });
        return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lazysizes/5.1.2/lazysizes.min.js';
    script.defer = true;
    document.body.append(script);
}

function openAnchorAccordion() {
    if (!window.location.hash) return;
    let target;
    try {
        target = document.querySelector(window.location.hash);
    } catch (_error) {
        return;
    }
    const link = target?.querySelector('.panel-title > a.collapsed');
    link?.click();
}

function scrollToHash() {
    if (!window.location.hash) return;
    let target;
    try {
        target = document.querySelector(window.location.hash);
    } catch (_error) {
        return;
    }
    if (target) {
        window.setTimeout(() => target.scrollIntoView({behavior: 'smooth'}), 500);
    }
}

function fixCookieConsentInitialFocus() {
    const banner = document.querySelector('#supi__banner');
    if (!banner || banner.dataset.initialFocusFixed === 'true' || (!banner.open && !banner.matches('[open]'))) return;
    banner.dataset.initialFocusFixed = 'true';
    banner.setAttribute('tabindex', '-1');
    window.setTimeout(() => banner.focus({preventScroll: true}), 0);
}

function initCookieConsentFocus() {
    ['supiInitEnd', 'supiBannerShow', 'bannerShow'].forEach((eventName) => {
        document.addEventListener(eventName, fixCookieConsentInitialFocus);
    });
    const root = document.querySelector('#supi') || document.body;
    new MutationObserver(fixCookieConsentInitialFocus).observe(root, {subtree: true, attributes: true, attributeFilter: ['open']});
    fixCookieConsentInitialFocus();
}

function initNewsletterPageError() {
    document.querySelectorAll('main form.powermail_form_1').forEach((form) => {
        const error = form.querySelector(':scope > .powermail_message_error');
        const field = form.querySelector('.powermail_fieldwrap_email');
        const label = field?.querySelector(':scope > label');
        if (!error || !field || !label) return;

        error.classList.add('powermail-message-inline-error');
        label.after(error);
        error.querySelectorAll('.powermail-message-field').forEach((fieldLabel) => fieldLabel.remove());
    });
}

export function initFrontend() {
    initCarousels();
    initHeaderSearch();
    initExternalMedia();
    initToTop();
    initSkipLinks();
    initResponsiveTables();
    initFilters();
    initVideoModals();
    initLazyImages();
    initCookieConsentFocus();
    initNewsletterPageError();
    openAnchorAccordion();
    scrollToHash();
    window.addEventListener('hashchange', openAnchorAccordion);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFrontend, {once: true});
} else {
    initFrontend();
}
